import "bootstrap/dist/css/bootstrap.min.css";
import { motion } from "framer-motion";
import styled, { createGlobalStyle, css } from "styled-components";
import { frostedGlassBg } from "./components/shared/style";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
} from "./constants/design/colors";
import { BORDER_RADIUS, COMPONENTS } from "./constants/design/spacing";

// NOTE: the Google Fonts at-rule import was moved to a <link> in
// public/index.html. styled-components injects createGlobalStyle via CSSOM
// insertRule() in production, where such an at-rule that is not the first rule
// THROWS and aborts injection of every rule inserted after it — which left
// React-portal modals (OFAC gate, Complete Sign In, Connect Wallet) unstyled and
// top-left in prod while the page itself (injected earlier) looked fine. Loading
// the font via a <link> removes it entirely. Do NOT re-add one here.
//
// This note lives OUTSIDE the template literal on purpose. styled-components
// scans the raw template strings for the literal token, comments included, and
// warns "Please do not use @import CSS syntax in createGlobalStyle" — so the
// comment that documents the fix was itself triggering the warning, twice, since
// StrictMode double-invokes the render body that checks.
export const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
    font-feature-settings: "tnum";
  }

  /* Monospace numbers for trading data */
  .mono, [data-mono] {
    font-family: 'JetBrains Mono', 'SF Mono', 'Fira Code', monospace;
    font-feature-settings: "tnum";
  }

  body {
    background: ${BACKGROUND_COLORS.five};
    color: white;
  }

  /* Thin styled scrollbars for data-dense areas.
   *
   * This said "thin" in the comment and "none" in the declaration. The result
   * was a split product: Chromium (and older WebKit) got the carefully styled
   * 6px bar defined immediately below, while Firefox — and Chromium 121+, which
   * honours scrollbar-width — got no scrollbar at all, across 23 scrollable
   * areas. The scrollbar-color line beneath it was dead too: it has no effect
   * once the bar is hidden.
   *
   * DESIGN.md is unambiguous here — "Never hide scrollbars globally" — and
   * sanctions hiding only on decorative horizontal strips, which opt out
   * individually (HorizontalScroller's hideScrollbar prop, TradeForm's tab
   * strip). */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${LAYER_COLORS.four} transparent;
  }

  *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  *::-webkit-scrollbar-track {
    background: transparent;
  }
  *::-webkit-scrollbar-thumb {
    background: ${LAYER_COLORS.four};
    border-radius: 3px;
  }
  *::-webkit-scrollbar-thumb:hover {
    background: ${BORDER_COLORS.three};
  }

  /* Exclude Para wallet-modal buttons from this reset. Para's utilities live in an
     @layer (Tailwind v4), and this UNLAYERED reset beats any layered rule regardless
     of specificity — so it was stripping the modal's bg-primary / border / sizing.
     Para buttons all carry \`para:\`-prefixed classes.
     The exclusion is wrapped in :where() so it adds ZERO specificity: the rule stays
     at plain \`button\` (0,0,1), leaving every other app button exactly as before. */
  button:not(:where([class*="para:"])) {
    border: none;
    background-color: transparent;
    padding: 0;

    &:disabled {
      cursor: not-allowed;
    }
  }

  /* Focus indication.
   *
   * Two problems were visible here. Buttons and links fell through to the
   * browser/Bootstrap default, which on this dark theme draws a hard WHITE
   * rectangle — visible as a box around "Docs" in the More menu after clicking
   * it, since a click leaves the element focused. And inputs had their outline
   * removed outright, which silently removes focus indication for keyboard
   * users along with it.
   *
   * focus-visible is the distinction the browser already computes: pointer
   * interaction gets no ring, keyboard navigation gets one. So the box stops
   * appearing on click WITHOUT anyone losing the ability to see where they are.
   * The ring uses the one accent colour rather than white (DESIGN.md: restrained,
   * one accent, no decoration noise). */
  :focus:not(:focus-visible) {
    outline: none;
  }

  :focus-visible:not(:where([data-para-role="modal"], [data-para-role="modal"] *)) {
    outline: 2px solid ${COLORS.highlight.one};
    outline-offset: 2px;
    border-radius: ${BORDER_RADIUS.sm}px;
  }

  // GLOBAL BOOTSTRAP OVERRIDES
  .container {
    max-width: unset;
  }

  .row {
    padding: 0;
  }

  [class*="col-"] {
    padding: 0;
  }

  .modal-backdrop.show {
    opacity: 0.8;
  }

  .dropdown-menu {
    min-width: 100%;
  }

  ul {
    margin: 0;
  }
`;

export const Scrim = styled(motion.div)`
  position: absolute;
  top: ${COMPONENTS.header.main}px;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${BACKGROUND_COLORS.thirteen};
  z-index: 999;
`;

export const foregroundComponent = css`
  border-radius: ${BORDER_RADIUS.five}px;
  background-color: ${LAYER_COLORS.one};
`;

export const PortalWrapper = styled.div<{ showOverlay?: boolean }>`
  /* Fixed (viewport-anchored), not absolute: the modal backdrop must always
     cover the full screen and center its modal regardless of how tall the app
     content is. With absolute the overlay collapsed to a short box whenever the
     page content was empty (e.g. an API/CORS error leaves the trading page with
     no data), pushing modals like the OFAC gate to the top-left. */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 5;

  ${({ showOverlay }) =>
    showOverlay
      ? css`
          ${frostedGlassBg(COLORS.black.two, COLORS.black.two)}
        `
      : css`
          pointer-events: none;
          z-index: -1;
        `}
`;
